import {flatOuterCardSx, flatOuterCardContentSx} from '../../../utils/mobileCardSx';
import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    Pagination,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {alpha} from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import UploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useTranslation} from 'react-i18next';
import {useMonthlyChange} from '../../../hooks/useMonthlyChange';
import {formatPeriodLabel} from '../../../utils/periodLabel';
import MonthlyChangeDataTable from './tables/MonthlyChangeDataTable';
import MonthlyChangeDataTableMobile from './mobile/MonthlyChangeDataTableMobile';
import MonthlyChangeItemsDialog from './dialogs/MonthlyChangeItemsDialog';
import MonthlyChangeColumnsDialog from './dialogs/MonthlyChangeColumnsDialog';
import DeleteMonthlyChangeDialog from './dialogs/DeleteMonthlyChangeDialog';

const outlinedSecondarySx = {borderWidth: '1px', borderRadius: '8px', '&:hover': {borderWidth: '1px'}};
const saveButtonSx = {
    color: '#fff',
    '&.Mui-disabled': {
        color: '#fff',
        opacity: 0.5,
        background: 'linear-gradient(135deg, #4CAF82 0%, #6BC49A 100%)',
    },
};

export default function MonthlyChange(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        selectedPeriod,
        selectedPeriodValue,
        periodError,
        description,
        setDescription,
        type,
        setType,
        calcType,
        setCalcType,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        isLoading,
        isPeriodLocked,
        deleteConfirmOpen,
        setDeleteConfirmOpen,
        deleteItemId,
        setDeleteItemId,

        isTableDataLoading,
        tableDataCurrentPage,
        setTableDataCurrentPage,
        tableDataItemsPerPage,

        activeCellId,
        handleCellActivate,
        handleCellValueChange,

        isFormDisabled,
        hasActiveEdits,
        pendingEditsCount,

        employeeSearchQuery,
        setEmployeeSearchQuery,
        onlyWithChanges,
        setOnlyWithChanges,
        basePayColumn,
        getEmployeeName,
        getEmployeeInitials,
        getBasePayDisplay,
        getColumnTreatmentSource,
        editableColumns,
        visibleEditableColumns,
        visibleColumnIds,
        filteredTableRows,
        columnTotals,
        categoryTotals,
        getCellDisplayValue,
        handleToggleColumnVisibility,
        handleShowAllColumns,
        columnsDialogOpen,
        setColumnsDialogOpen,

        itemsDialogOpen,
        setItemsDialogOpen,
        itemsSearchQuery,
        setItemsSearchQuery,
        itemsTypeFilter,
        setItemsTypeFilter,
        itemsBasisFilter,
        setItemsBasisFilter,
        itemCounts,
        filteredMonthlyChangeItems,
        addItemFormOpen,
        setAddItemFormOpen,

        handleAddMonthlyChange,
        handleDeleteMonthlyChange,
        handleAddEdits,
        handleCancelEdits,
    } = useMonthlyChange();

    const periodLabel = formatPeriodLabel(t, selectedPeriod, selectedPeriodValue);
    const statusLabel = isPeriodLocked ? t('dashboard.wizard.status.completed') : t('dashboard.wizard.status.inProgress');

    const paginatedRows = filteredTableRows.slice(
        (tableDataCurrentPage - 1) * tableDataItemsPerPage,
        tableDataCurrentPage * tableDataItemsPerPage,
    );

    return (
        <Box sx={{width: '100%', pb: isMobile ? 10 : 3}}>
            {periodError && <Alert severity="error" sx={{mb: 2}}>{periodError}</Alert>}

            <Card
                variant="outlined"
                sx={{
                    borderRadius: 1,
                    mb: 2,
                    backgroundColor: (t2) => alpha(isPeriodLocked ? t2.palette.success.main : t2.palette.warning.main, 0.06),
                    borderColor: isPeriodLocked ? 'success.light' : 'warning.light',
                }}
            >
                <CardContent sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5}}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <CalendarMonthIcon sx={{color: isPeriodLocked ? 'success.main' : 'warning.main'}}/>
                        <Typography variant="h6" fontWeight={700}>{periodLabel || '—'}</Typography>
                        <Chip label={statusLabel} size="small" color={isPeriodLocked ? 'success' : 'warning'} variant="outlined"/>
                    </Stack>
                    {pendingEditsCount > 0 && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{width: 8, height: 8, borderRadius: '50%', backgroundColor: 'warning.main'}}/>
                            <Typography variant="body2" sx={{color: 'warning.dark', fontWeight: 600}}>
                                {t('payroll.monthlyChange.unsavedChanges', {count: pendingEditsCount})}
                            </Typography>
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {isPeriodLocked && (
                <Alert severity="warning" sx={{mb: 2}}>
                    {t('workEvidence.periodLockedWarning')}
                </Alert>
            )}

            <Card variant="outlined" sx={flatOuterCardSx(isMobile)}>
                <CardContent sx={flatOuterCardContentSx(isMobile)}>
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        justifyContent="space-between"
                        alignItems={{xs: 'flex-start', sm: 'center'}}
                        spacing={1}
                        sx={{mb: 2}}
                    >
                        <Typography variant="h6" fontWeight={700}>
                            {t('payroll.monthlyChange.employeeChangesTitle')}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Typography variant="body2" color="text.secondary">
                                {t('payroll.monthlyChange.activeItemsCount', {count: itemCounts.all})}
                            </Typography>
                            {!isMobile && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<TuneIcon/>}
                                    onClick={() => setItemsDialogOpen(true)}
                                    sx={outlinedSecondarySx}
                                >
                                    {t('payroll.monthlyChange.manageItemsButton')}
                                </Button>
                            )}
                            {!isMobile && (
                                <>
                                    <Tooltip title={t('common.notAvailableYet')}>
                                        <span>
                                            <Button variant="outlined" startIcon={<UploadIcon/>} disabled sx={outlinedSecondarySx} color="inherit">
                                                {t('payroll.monthlyChange.importButton')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleAddEdits}
                                        disabled={!hasActiveEdits}
                                        sx={saveButtonSx}
                                    >
                                        {t('payroll.monthlyChange.saveChangesButton')}
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Stack>

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mb: 2}}>
                        <TextField
                            fullWidth
                            size="small"
                            value={employeeSearchQuery}
                            onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                            placeholder={t('payroll.monthlyChange.searchEmployeePlaceholder')}
                            InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>}}
                        />
                        <FormControlLabel
                            sx={{whiteSpace: 'nowrap', ml: {xs: 0, sm: 1}}}
                            control={<Switch checked={onlyWithChanges} onChange={(e) => setOnlyWithChanges(e.target.checked)}/>}
                            label={t('payroll.monthlyChange.onlyWithChanges')}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<ViewColumnIcon/>}
                            onClick={() => setColumnsDialogOpen(true)}
                            sx={{whiteSpace: 'nowrap', flexShrink: 0}}
                        >
                            {t('payroll.monthlyChange.selectColumnsButton')}
                        </Button>
                    </Stack>

                    {isMobile && (
                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            startIcon={<TuneIcon/>}
                            onClick={() => setItemsDialogOpen(true)}
                            sx={{...outlinedSecondarySx, mb: 2}}
                        >
                            {t('payroll.monthlyChange.manageItemsButton')} ({itemCounts.all})
                        </Button>
                    )}

                    {isLoading || isTableDataLoading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : !selectedPeriod ? (
                        <Box sx={{textAlign: 'center', py: 6}}>
                            <Typography variant="body1" color="text.secondary">{t('payroll.monthlyChange.noPeriodSelected')}</Typography>
                            <Typography variant="body2" color="text.disabled">{t('payroll.monthlyChange.selectPeriodAbove')}</Typography>
                        </Box>
                    ) : (
                        <>
                            {isMobile ? (
                                <MonthlyChangeDataTableMobile
                                    basePayColumn={basePayColumn}
                                    visibleEditableColumns={visibleEditableColumns}
                                    rows={paginatedRows}
                                    getCellDisplayValue={getCellDisplayValue}
                                    getEmployeeName={getEmployeeName}
                                    getEmployeeInitials={getEmployeeInitials}
                                    getBasePayDisplay={getBasePayDisplay}
                                    getColumnTreatmentSource={getColumnTreatmentSource}
                                    activeCellId={activeCellId}
                                    handleCellValueChange={handleCellValueChange}
                                    handleCellActivate={handleCellActivate}
                                    isPeriodLocked={isPeriodLocked}
                                />
                            ) : (
                                <MonthlyChangeDataTable
                                    basePayColumn={basePayColumn}
                                    visibleEditableColumns={visibleEditableColumns}
                                    rows={paginatedRows}
                                    getCellDisplayValue={getCellDisplayValue}
                                    getEmployeeName={getEmployeeName}
                                    getEmployeeInitials={getEmployeeInitials}
                                    getBasePayDisplay={getBasePayDisplay}
                                    getColumnTreatmentSource={getColumnTreatmentSource}
                                    columnTotals={columnTotals}
                                    activeCellId={activeCellId}
                                    handleCellValueChange={handleCellValueChange}
                                    handleCellActivate={handleCellActivate}
                                    isPeriodLocked={isPeriodLocked}
                                />
                            )}

                            {filteredTableRows.length > tableDataItemsPerPage && (
                                <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}>
                                    <Pagination
                                        count={Math.ceil(filteredTableRows.length / tableDataItemsPerPage)}
                                        color="primary"
                                        page={tableDataCurrentPage}
                                        onChange={(_e, page) => setTableDataCurrentPage(page)}
                                    />
                                </Box>
                            )}

                            <Stack
                                direction={{xs: 'column', sm: 'row'}}
                                justifyContent={isMobile ? 'flex-end' : 'space-between'}
                                alignItems={{xs: 'flex-start', sm: 'center'}}
                                spacing={1}
                                sx={{mt: 2}}
                            >
                                {!isMobile && (
                                    <Typography variant="caption" color="text.secondary">
                                        📌 {t('payroll.monthlyChange.pinnedHint')} &nbsp; ⟷ {t('payroll.monthlyChange.scrollHint')}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {t('payroll.monthlyChange.employeeCount', {count: filteredTableRows.length})}
                                </Typography>
                            </Stack>

                            {isMobile && editableColumns.length > 0 && (
                                <Stack direction="row" spacing={1.5} sx={{mt: 2, overflowX: 'auto'}}>
                                    {[
                                        {label: t('payroll.monthlyChange.treatment.allowanceGross'), value: categoryTotals.allowanceGross},
                                        {label: t('payroll.monthlyChange.treatment.allowanceNet'), value: categoryTotals.allowanceNet},
                                        {label: t('payroll.monthlyChange.treatment.deductionNet'), value: categoryTotals.deductionNet},
                                    ].map((stat) => (
                                        <Card key={stat.label} variant="outlined" sx={{borderRadius: 1, flex: 1, minWidth: 110}}>
                                            <CardContent sx={{textAlign: 'center', p: 1.5, '&:last-child': {pb: 1.5}}}>
                                                <Typography variant="caption" color="text.secondary" noWrap>{stat.label}</Typography>
                                                <Typography variant="subtitle1" fontWeight={700}>{stat.value.toFixed(2)} €</Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            )}

                            {isMobile && (
                                <Tooltip title={t('common.notAvailableYet')}>
                                    <span>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<UploadIcon/>}
                                            disabled
                                            sx={{...outlinedSecondarySx, mt: 2}}
                                        >
                                            {t('payroll.monthlyChange.importButton')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}

                            {!isMobile && hasActiveEdits && (
                                <Stack direction="row" justifyContent="flex-end" sx={{mt: 2}}>
                                    <Button variant="text" onClick={handleCancelEdits}>
                                        {t('common.actions.cancel')}
                                    </Button>
                                </Stack>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Alert severity="info" icon={<InfoOutlinedIcon/>} sx={{mt: 2}}>
                {t('payroll.monthlyChange.footerNote', {period: periodLabel})}
            </Alert>

            {isMobile && (
                <Box sx={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, p: 1.5,
                    backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
                    zIndex: (t2) => t2.zIndex.appBar,
                }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={handleAddEdits}
                        disabled={!hasActiveEdits}
                        sx={saveButtonSx}
                    >
                        {t('payroll.monthlyChange.saveChangesButton')}
                    </Button>
                </Box>
            )}

            <MonthlyChangeItemsDialog
                open={itemsDialogOpen}
                onClose={() => setItemsDialogOpen(false)}
                periodLabel={periodLabel}
                items={filteredMonthlyChangeItems}
                itemsSearchQuery={itemsSearchQuery}
                setItemsSearchQuery={setItemsSearchQuery}
                itemsTypeFilter={itemsTypeFilter}
                setItemsTypeFilter={setItemsTypeFilter}
                itemsBasisFilter={itemsBasisFilter}
                setItemsBasisFilter={setItemsBasisFilter}
                addItemFormOpen={addItemFormOpen}
                setAddItemFormOpen={setAddItemFormOpen}
                description={description}
                setDescription={setDescription}
                type={type}
                setType={setType}
                calcType={calcType}
                setCalcType={setCalcType}
                handleAddMonthlyChange={handleAddMonthlyChange}
                isFormDisabled={isFormDisabled}
                setDeleteItemId={setDeleteItemId}
                setDeleteConfirmOpen={setDeleteConfirmOpen}
            />

            <MonthlyChangeColumnsDialog
                open={columnsDialogOpen}
                onClose={() => setColumnsDialogOpen(false)}
                editableColumns={editableColumns}
                visibleColumnIds={visibleColumnIds}
                getColumnTreatmentSource={getColumnTreatmentSource}
                handleToggleColumnVisibility={handleToggleColumnVisibility}
                handleShowAllColumns={handleShowAllColumns}
            />

            <DeleteMonthlyChangeDialog
                open={deleteConfirmOpen}
                deleteItemId={deleteItemId}
                setDeleteConfirmOpen={setDeleteConfirmOpen}
                setDeleteItemId={setDeleteItemId}
                handleDeleteMonthlyChange={handleDeleteMonthlyChange}
            />

            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert severity="success" onClose={() => setShowSuccessMessage(false)} sx={{width: '100%'}}>
                    {successMessageText}
                </Alert>
            </Snackbar>
        </Box>
    );
}
